import React, { useId } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { Icon, Icons } from './red/Icons';
import { NavGlyph } from './red/NavGlyph';

// Neutrale Glass-Insel (folgt Theme: dunkel/hell). Rot kehrt als gezielter
// Akzent zurück: aktiver Tab rot hinterlegt, FAB rot — sonst neutral.
const RED_BTN = '#E4544A'; // Marken-Koralle (flach, Tiefe via Schatten)
const SPRING = 'cubic-bezier(.16,1,.3,1)';
const BLUR = 'blur(30px) saturate(180%)';

function navGlass(isDark) {
  return isDark
    ? { bg: 'rgba(18,20,26,0.86)',  line: 'rgba(255,255,255,0.07)', edge: 'rgba(255,255,255,0.04)' }
    : { bg: 'rgba(220,220,218,0.94)', line: 'rgba(0,0,0,0.07)',    edge: 'rgba(255,255,255,0.80)' };
}

function NavBtn({ id, label, active, onClick, sub, thumbId, tourId, activeFill, activeStroke }) {
  return (
    <button
      onClick={() => onClick(id)}
      className="iosPress"
      data-tour={tourId}
      aria-label={label}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        padding: '8px 4px',
        cursor: 'pointer',
        position: 'relative',
        // Aktiver Button bewusst unter den inaktiven (z:0<1), damit der gleitende
        // Thumb beim Wechsel nie über fremde Icons läuft (wie im Segmented).
        zIndex: active ? 0 : 1,
        WebkitTapHighlightColor: 'transparent',
        transition: `transform 0.15s ${SPRING}`,
      }}
    >
      {/* Aktiv = rot hinterlegte Pille (gleitet via layoutId) + weißes Icon */}
      <span style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 46, height: 40,
      }}>
        {active && (
          <motion.div
            layoutId={thumbId}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
            style={{ position: 'absolute', inset: 0, borderRadius: 13, background: activeFill, zIndex: 0 }}
          />
        )}
        <span style={{ position: 'relative', zIndex: 1, display: 'flex' }}>
          <NavGlyph id={id} active={false} stroke={active ? activeStroke : sub} size={24} sw={1.7} />
        </span>
      </span>
    </button>
  );
}

export default function BottomNav({ active, onChange }) {
  const { T, isDark } = useTheme();
  const { t } = useT();
  const g = navGlass(isDark);
  const thumbId = useId(); // gleitender Aktiv-Thumb (framer layoutId)

  return (
    <div
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        margin: '0 auto',
        maxWidth: 460,
        height: 66,
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
        background: g.bg,
        backdropFilter: BLUR,
        WebkitBackdropFilter: BLUR,
        boxShadow: `inset 0 1px 0 ${g.edge}, inset 0 0 0 1px ${g.line}, ${isDark ? '0 14px 34px rgba(0,0,0,0.26)' : '0 10px 30px rgba(60,48,28,0.14)'}`,
        borderRadius: 30,
        zIndex: 100,
        // Sanftes Einblenden beim Mount (z.B. Swipe-back von einer Detail-Seite,
        // wo die Nav weg war) — gleitet von unten hoch statt abrupt aufzuploppen.
        animation: 'navIn 320ms cubic-bezier(0.16,1,0.3,1) backwards',
      }}
    >
      <style>{`.iosPress:active{transform:scale(.88)!important}.fabPress:active{transform:translateX(-50%) scale(.9)!important}@keyframes navIn{from{opacity:0;transform:translateY(130%)}to{opacity:1;transform:translateY(0)}}@media (prefers-reduced-motion: reduce){[style*="navIn"]{animation:none!important}}`}</style>
      <NavBtn id="dashboard" label={t('dashboard')} active={active === 'dashboard'} onClick={onChange} sub={T.sub} activeFill={T.glassHigh} activeStroke={T.text} thumbId={thumbId} tourId="nav-dashboard" />
      <NavBtn id="wallet" label={t('transactions')} active={active === 'wallet'} onClick={onChange} sub={T.sub} activeFill={T.glassHigh} activeStroke={T.text} thumbId={thumbId} tourId="nav-wallet" />

      {/* Mitte: Platzhalter, der FAB schwebt darüber */}
      <div style={{ width: 62, flexShrink: 0 }} />

      <NavBtn id="reports" label={t('reports')} active={active === 'reports'} onClick={onChange} sub={T.sub} activeFill={T.glassHigh} activeStroke={T.text} thumbId={thumbId} tourId="nav-reports" />
      <NavBtn id="settings" label={t('profile')} active={active === 'settings'} onClick={onChange} sub={T.sub} activeFill={T.glassHigh} activeStroke={T.text} thumbId={thumbId} tourId="nav-settings" />

      {/* Roter FAB — der eine Akzent, mittig erhöht (kein Ring, nur Schatten) */}
      <button
        onClick={() => onChange('add')}
        className="fabPress"
        data-tour="nav-fab-add"
        style={{
          position: 'absolute',
          left: '50%',
          top: 4,
          transform: 'translateX(-50%)',
          width: 54,
          height: 54,
          borderRadius: '50%',
          background: RED_BTN,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Alte Mobile-Navbar zurück (User: „war schöner"): runder FAB (50%)
          // mit rot-getöntem Glow + neutralem Tiefen-Schatten.
          boxShadow: '0 6px 16px rgba(120,20,20,0.30), 0 2px 6px rgba(0,0,0,0.18)',
          WebkitTapHighlightColor: 'transparent',
          zIndex: 2,
          transition: `transform 0.16s ${SPRING}`,
        }}
      >
        <Icon d={Icons.plus} size={24} stroke="#fff" sw={2.6} />
      </button>
    </div>
  );
}
